<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * The honesty component.
 *
 * U-Observe ships a real product surface, not a demo. Anything on the roadmap but not
 * built is rendered through this component: visibly muted, explicitly labelled
 * "Not implemented", never clickable, and always naming the slice it lands in. It must
 * never navigate anywhere — a pretend page that loads and does nothing is worse than an
 * honest dead stop, because it costs an operator their trust the first time they try it.
 */
const props = withDefaults(
  defineProps<{
    /** What the feature is called in the product, e.g. 'Alerts'. */
    feature: string
    /** Delivery slice, e.g. 'Slice 3'. Shown in the tooltip and to screen readers. */
    slice?: string
    variant?: 'nav' | 'inline' | 'block'
    icon?: string
    /** Extra context, e.g. what it will do when it exists. */
    detail?: string
  }>(),
  { slice: '', variant: 'inline', icon: '', detail: '' },
)

const { t } = useI18n()

const explanation = computed(() => {
  const base = t('ui.notImplemented.explanation', {
    feature: props.feature,
    slice: props.slice || t('ui.notImplemented.laterSlice'),
  })
  return props.detail ? `${base} ${props.detail}` : base
})
</script>

<!-- NOTE: the root element is deliberately not a <button>, an <a> or a <RouterLink> —
     there is nothing to navigate to. Keep this comment outside <template> so the root
     element stays the component's first node. -->
<template>
  <div
    :class="['not-implemented', `not-implemented--${variant}`]"
    :title="explanation"
    aria-disabled="true"
    role="note"
    tabindex="0"
  >
    <span v-if="icon" class="not-implemented__icon" aria-hidden="true">{{ icon }}</span>
    <span class="not-implemented__label">
      <slot>{{ feature }}</slot>
    </span>
    <span class="not-implemented__tag">{{ t('common.notImplemented.badge') }}</span>
    <span class="sr-only">. {{ explanation }}</span>
    <p v-if="variant === 'block' && detail" class="not-implemented__detail">{{ detail }}</p>
  </div>
</template>

<style scoped>
.not-implemented {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--text-muted);
  cursor: not-allowed;
  /* Hatching: reads as "unavailable" even in greyscale. */
  background-image: repeating-linear-gradient(
    135deg,
    transparent,
    transparent 5px,
    var(--surface-2) 5px,
    var(--surface-2) 6px
  );
}

.not-implemented--nav {
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius);
  font-size: var(--text-sm);
  border: 1px dashed var(--border-subtle);
}

.not-implemented--inline {
  display: inline-flex;
  padding: 1px var(--space-2);
  border-radius: var(--radius-sm);
  border: 1px dashed var(--border);
  font-size: var(--text-xs);
}

.not-implemented--block {
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-1);
  padding: var(--space-4);
  border: 1px dashed var(--border);
  border-radius: var(--radius);
}

.not-implemented__icon {
  width: 18px;
  text-align: center;
  flex-shrink: 0;
  opacity: 0.7;
}

.not-implemented__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.not-implemented__tag {
  flex-shrink: 0;
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 1px 5px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border);
  background: var(--surface-3);
  color: var(--text-muted);
}

.not-implemented__detail {
  font-size: var(--text-xs);
  color: var(--text-muted);
  max-width: 60ch;
}

.not-implemented:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}
</style>
